// Global image loading queue to limit concurrent requests
export class ImageLoadingQueue {
  private queue: Array<() => Promise<void>> = [];
  private processing: Set<string> = new Set();
  private maxConcurrent = 3; // Limit concurrent image loads
  private cache = new Map<string, string>();

  async loadImage(url: string, key: string): Promise<string | null> {
    // Check cache first
    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    return new Promise((resolve) => {
      const loadTask = async () => {
        if (this.processing.has(key)) {
          resolve(null);
          return;
        }

        this.processing.add(key);

        try {
          const img = new Image();

          const loadPromise = new Promise<string | null>((imgResolve) => {
            img.onload = () => {
              this.cache.set(key, url);
              imgResolve(url);
            };
            img.onerror = () => imgResolve(null);
          });

          img.src = url;
          const result = await loadPromise;
          resolve(result);
        } catch (error) {
          resolve(null);
        } finally {
          this.processing.delete(key);
          this.processNext();
        }
      };

      this.queue.push(loadTask);
      this.processNext();
    });
  }

  private processNext() {
    if (this.processing.size < this.maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift();
      if (task) {
        task();
      }
    }
  }
}

// Singleton instance
export const imageLoadingQueue = new ImageLoadingQueue();
